/* eslint-disable react-hooks/exhaustive-deps */
import { FC, PropsWithChildren, useEffect, useState } from 'react';

import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';

import { IconMinus, IconPlus } from 'icons';

import styles from './Accordion.module.css';

type Item = {
  title: string;
  text: string;
  imageUrl?: string;
};

type AccordionItemProps = PropsWithChildren<{
  title: string;
  isExpanded: boolean;
  handleClick: () => void;
  size: 'large' | 'small';
  colorSheme: string;
  index: number;
  text: string;
  imageUrl?: string;
}>;

const AccordionItem: FC<AccordionItemProps> = ({
  title,
  isExpanded,
  handleClick,
  size,
  text,
  imageUrl,
  children,
  colorSheme,
  index,
}) => {
  // eslint-disable-next-line no-nested-ternary
  const height = isExpanded ? 'auto' : 0;
  const style = index === 0 ? {} : { borderTop: `2px solid ${colorSheme}` };

  return (
    <div
      style={{
        ...style,
        overflow: 'hidden',
      }}
      className={classNames(styles.item, styles[`item_${size}`])}
    >
      <div
        role="presentation"
        className={classNames(
          styles.item_header,
          styles[`item_header_${size}`],
        )}
        onClick={handleClick}
      >
        <div>{title}</div>
        {isExpanded ? (
          <IconMinus color={colorSheme} size={size === 'large' ? 'l' : 's'} />
        ) : (
          <IconPlus color={colorSheme} size={size === 'large' ? 'l' : 's'} />
        )}
      </div>

      <AnimateHeight duration={500} height={height} animateOpacity>
        <dd className={styles[`item_content_${size}`]}>{children}</dd>
        {isExpanded && !imageUrl
          ? !children && <p className={styles.item_content_content}>{text}</p>
          : imageUrl && (
              <>
                <p>{text}</p>
                <img className={styles.image_small} src={imageUrl} alt="tab" />
              </>
            )}
      </AnimateHeight>
    </div>
  );
};

type AccordionProps = {
  items: Item[];
  size: 'large' | 'small';
  defaultIndex?: number | null;
  onChange?: (index: number) => void;
  colorSheme?: string;
  play?: boolean;
};

export const Accordion: FC<AccordionProps> = ({
  items,
  defaultIndex = null,
  colorSheme = 'black',
  size,
  play,
  onChange,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    defaultIndex,
  );

  const changeItemHandler = (itemIndex: number) => {
    let newIndex = itemIndex === expandedIndex ? null : itemIndex;
    if (size === 'small') {
      newIndex = itemIndex;
    }
    setExpandedIndex(newIndex);
    if (onChange) onChange(itemIndex);
  };

  useEffect(() => {
    if (play && defaultIndex !== expandedIndex) {
      setExpandedIndex(defaultIndex);
    }
  }, [defaultIndex]);

  return (
    <div className={styles.container}>
      {items.map((item, index) => (
        <AccordionItem
          key={item.title}
          title={item.title}
          size={size}
          text={item.text}
          imageUrl={item?.imageUrl}
          isExpanded={expandedIndex === index}
          colorSheme={colorSheme}
          index={index}
          handleClick={() => changeItemHandler(index)}
        >
          {size === 'large' ? <div>{item.text}</div> : null}
        </AccordionItem>
      ))}
    </div>
  );
};
